/**
 * ContextUsersTab — Shared tab component for managing context-users
 *
 * Used in Canal, Funil and Pipeline de Ticket modals/pages. Provides:
 * - List of current users with role/scope badges
 * - Inline editing of role/scope (click FiEdit icon on each card)
 * - Add user modal with role/scope selection
 * - Remove user action
 *
 * Mirrors the existing `OperatorsSection` from channels but is generic
 * and works with any context via `useContextUsers` hook.
 *
 * Ver plano: DOCS/architecture/ASSIGNMENTS_REMOVAL.md §4.13
 */

import React, { useMemo, useState } from 'react';
import { FiEdit, FiCheck, FiX } from 'react-icons/fi';
import { Button } from '@/shared/components/ui/Button';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Badge } from '@/shared/components/ui/badge';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { DeleteConfirmationModal } from '@/shared/components/ui/DeleteConfirmationModal';
import { Select } from '@/shared/components/ui/Select';
import { Tooltip } from '@/shared/components/ui/Tooltip';
import { Icon } from '@/shared/icons';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import {
  useContextUsers,
  type ContextRef,
  type ContextUser,
} from '@/shared/hooks/useContextUsers';

interface ContextUsersTabProps {
  contextRef: ContextRef;
  /** Oculta colunas de role e scope (ex: shifts que não possuem esse conceito) */
  hideRoleScope?: boolean;
}

const roleLabels: Record<string, string> = {
  attendant: 'Atendente',
  viewer: 'Visualizador',
  manager: 'Gestor',
};

const scopeLabels: Record<string, string> = {
  own: 'Próprios',
  team: 'Da Equipe',
  all: 'Todos',
};

const roleOptions = [
  { value: 'attendant', label: 'Atendente — recebe distribuição automática' },
  { value: 'viewer', label: 'Visualizador — apenas visualiza' },
  { value: 'manager', label: 'Gestor — vê tudo, fora do rodízio, recebe alertas de gestão' },
];

const scopeOptions = [
  { value: 'all', label: 'Todos — vê tudo do contexto' },
  { value: 'team', label: 'Da Equipe — vê itens do time + fila pública' },
  { value: 'own', label: 'Próprios — vê apenas seus itens + fila pública' },
];

const contextNoun: Record<string, string> = {
  channel: 'canal',
  funnel: 'funil',
  pipeline: 'pipeline',
  shift: 'turno',
};

// ----- Inline edit state per card -----
interface EditState {
  role: 'attendant' | 'viewer' | 'manager';
  scope: 'own' | 'team' | 'all';
}

export const ContextUsersTab: React.FC<ContextUsersTabProps> = ({
  contextRef,
  hideRoleScope = false,
}) => {
  const { users, isLoading, add, update, remove } = useContextUsers(contextRef);
  const { data: allCompanyUsers } = useUsersDropdown({ status: 'active' });

  // Add modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [newRole, setNewRole] = useState<'attendant' | 'viewer' | 'manager'>('attendant');
  const [newScope, setNewScope] = useState<'own' | 'team' | 'all'>('all');

  // Inline edit state — keyed by userId
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>({ role: 'attendant', scope: 'all' });
  const [isSaving, setIsSaving] = useState(false);

  // Remove confirmation modal state
  const [userToRemove, setUserToRemove] = useState<{ id: string; name: string } | null>(null);

  // userId pode vir como `userId` (funnels/pipelines) ou `id` (channels legado)
  const getUserId = (user: ContextUser): string => user.userId || user.id;

  const handleStartEdit = (user: ContextUser) => {
    setEditingUserId(getUserId(user));
    setEditState({ role: user.role, scope: user.scope });
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
  };

  const handleSaveEdit = async (userId: string) => {
    setIsSaving(true);
    try {
      await update.mutateAsync({ userId, updates: editState });
      setEditingUserId(null);
    } catch (err) {
      console.error('Erro ao atualizar usuário:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdd = async () => {
    if (!newUserId.trim()) return;
    try {
      await add.mutateAsync({
        userId: newUserId.trim(),
        role: newRole,
        scope: newScope,
      });
      setShowAddModal(false);
      setNewUserId('');
      setNewRole('attendant');
      setNewScope('all');
    } catch (err) {
      console.error('Erro ao adicionar usuário:', err);
    }
  };

  const handleConfirmRemove = async () => {
    if (!userToRemove) return;
    try {
      await remove.mutateAsync(userToRemove.id);
      setUserToRemove(null);
    } catch (err) {
      console.error('Erro ao remover usuário:', err);
    }
  };

  const getInitials = (user: ContextUser) => {
    const name = user.name || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email || '?';
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getDisplayName = (user: ContextUser) => {
    return user.name || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email || user.userId;
  };

  const noun = contextNoun[contextRef.kind] ?? 'contexto';

  // Opções de usuários disponíveis (exclui quem já está no contexto)
  const existingUserIds = useMemo(() => new Set(users.map((u) => u.userId || u.id)), [users]);
  const availableUserOptions = useMemo(() => {
    if (!allCompanyUsers) return [];
    return allCompanyUsers
      .filter((u) => !existingUserIds.has(u.id))
      .map((u) => ({
        value: u.id,
        label: u.name || u.email,
      }));
  }, [allCompanyUsers, existingUserIds]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="users" className="w-5 h-5" style={{ color: 'var(--theme-button-primary)' }} />
          <h3 className="font-semibold text-base" style={{ color: 'var(--theme-card-text)' }}>
            Usuários do {noun}
          </h3>
        </div>
        <Button onClick={() => setShowAddModal(true)} size="sm" data-testid="context-users-add-button">
          <Icon name="user-plus" className="w-4 h-4 mr-2" />
          Adicionar
        </Button>
      </div>

      {/* Info: contexto inativo se vazio */}
      {!isLoading && users.length === 0 && (
        <div
          className="flex items-start gap-3 p-3 rounded-lg"
          style={{
            backgroundColor: 'var(--theme-warning-background)',
            color: 'var(--theme-warning-color)',
          }}
        >
          <Icon name="info" className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium">Nenhum usuário vinculado</p>
            <p className="mt-1 opacity-90">
              Sem usuários cadastrados, este {noun} fica invisível para todos os
              membros da empresa e não distribui automaticamente. Adicione ao menos
              um usuário (qualquer papel) para ativar a visibilidade — a distribuição
              automática considera apenas os de papel &quot;atendente&quot;.
            </p>
          </div>
        </div>
      )}

      {/* User list */}
      {isLoading ? (
        <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
          Carregando...
        </p>
      ) : users.length > 0 ? (
        <div className="space-y-2">
          {users.map((user) => {
            const uid = getUserId(user);
            const isEditing = editingUserId === uid;

            return (
              <div
                key={uid}
                className="flex items-center justify-between p-3 rounded-lg"
                style={{ backgroundColor: 'var(--theme-input-background)' }}
              >
                {/* Avatar + nome */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* F83: avatar de pessoa = squircle (convenção do app) */}
                  <Avatar className="h-9 w-9 flex-shrink-0" isSquircle>
                    <AvatarImage src={user.avatar} isSquircle />
                    <AvatarFallback isSquircle>{getInitials(user)}</AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
                      {getDisplayName(user)}
                    </p>

                    {hideRoleScope ? null : isEditing ? (
                      /* ---- Modo edição: selects inline ---- */
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <div style={{ minWidth: 160 }}>
                          <Select
                            value={editState.role}
                            onChange={(value) =>
                              setEditState((prev) => ({ ...prev, role: value as 'attendant' | 'viewer' | 'manager' }))
                            }
                            options={[
                              { value: 'attendant', label: 'Atendente' },
                              { value: 'viewer', label: 'Visualizador' },
                              { value: 'manager', label: 'Gestor' },
                            ]}
                          />
                        </div>
                        <div style={{ minWidth: 140 }}>
                          <Select
                            value={editState.scope}
                            onChange={(value) =>
                              setEditState((prev) => ({ ...prev, scope: value as 'own' | 'team' | 'all' }))
                            }
                            options={[
                              { value: 'all', label: 'Todos' },
                              { value: 'team', label: 'Da Equipe' },
                              { value: 'own', label: 'Próprios' },
                            ]}
                          />
                        </div>
                      </div>
                    ) : (
                      /* ---- Modo visualização: badges ---- */
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="secondary" className="text-xs">
                          {roleLabels[user.role] ?? user.role}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {scopeLabels[user.scope] ?? user.scope}
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>

                {/* Ações */}
                <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                  {isEditing && !hideRoleScope ? (
                    <>
                      <Tooltip content="Salvar">
                        <button
                          type="button"
                          onClick={() => handleSaveEdit(uid)}
                          disabled={isSaving}
                          className="p-1.5 rounded-lg transition-colors"
                          style={{ color: '#8b5cf6' }}
                          data-testid={`context-users-save-${uid}`}
                        >
                          <FiCheck size={16} />
                        </button>
                      </Tooltip>
                      <Tooltip content="Cancelar">
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          disabled={isSaving}
                          className="p-1.5 rounded-lg transition-colors"
                          style={{ color: 'var(--theme-muted-text)' }}
                          data-testid={`context-users-cancel-edit-${uid}`}
                        >
                          <FiX size={16} />
                        </button>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      {!hideRoleScope && (
                        <Tooltip content="Editar função e escopo">
                          <button
                            type="button"
                            onClick={() => handleStartEdit(user)}
                            className="p-1.5 rounded-lg transition-colors"
                            style={{ color: 'var(--theme-muted-text)' }}
                            data-testid={`context-users-edit-${uid}`}
                          >
                            <FiEdit size={15} />
                          </button>
                        </Tooltip>
                      )}
                      <Tooltip content="Remover">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setUserToRemove({ id: uid, name: getDisplayName(user) })}
                          disabled={remove.isPending}
                          data-testid={`context-users-remove-${uid}`}
                        >
                          <Icon name="trash" className="w-4 h-4" style={{ color: 'var(--theme-error-color)' }} />
                        </Button>
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Add User Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} maxWidth="md">
        <ModalHeader onClose={() => setShowAddModal(false)}>
          <h2 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
            Adicionar Usuário
          </h2>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <Select
              label="Usuário"
              value={newUserId}
              onChange={(value) => setNewUserId(value)}
              options={availableUserOptions}
              placeholder={availableUserOptions.length === 0 ? 'Todos os usuários já foram adicionados' : 'Selecione um usuário'}
            />

            {!hideRoleScope && (
              <>
                <Select
                  label="Função"
                  value={newRole}
                  onChange={(value) => setNewRole(value as 'attendant' | 'viewer' | 'manager')}
                  options={roleOptions}
                />

                <Select
                  label="Escopo de Visualização"
                  value={newScope}
                  onChange={(value) => setNewScope(value as 'own' | 'team' | 'all')}
                  options={scopeOptions}
                />
              </>
            )}
          </div>
        </ModalContent>
        <ModalFooter>
          <Button onClick={() => setShowAddModal(false)} variant="secondary" data-testid="context-users-add-cancel">
            Cancelar
          </Button>
          <Button
            onClick={handleAdd}
            disabled={!newUserId.trim() || add.isPending}
            data-testid="context-users-add-confirm"
          >
            Adicionar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Remove Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={userToRemove !== null}
        onClose={() => setUserToRemove(null)}
        onConfirm={handleConfirmRemove}
        title="Remover usuário"
        message={`Tem certeza que deseja remover ${userToRemove?.name ?? ''} do ${noun}?`}
        confirmButtonText="Remover"
        loadingText="Removendo..."
        isLoading={remove.isPending}
      />
    </div>
  );
};

export default ContextUsersTab;
